import { useEffect, useRef, useCallback } from 'react'
import { Package } from 'lucide-react'
import { ProductCard, ProductRowCard } from './ProductCard'
import type { Product, ViewMode } from '@/types'

export interface ProductGridProps {
  products: Product[]
  onAddToCart: (product: Product) => void
  onProductClick: (product: Product) => void
  viewMode?: ViewMode
  focusedIndex?: number
  cartQuantities?: Map<number, number>
  // Infinite scroll props
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  fetchNextPage?: () => void
}

export function ProductGrid({
  products,
  onAddToCart,
  onProductClick,
  viewMode = 'grid',
  focusedIndex = -1,
  cartQuantities = new Map(),
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: ProductGridProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Scroll focused card into view
  useEffect(() => {
    if (focusedIndex >= 0 && containerRef.current) {
      const child = containerRef.current.children[focusedIndex] as HTMLElement
      child?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [focusedIndex])

  // Intersection observer for infinite scroll
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && fetchNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const element = loadMoreRef.current
    if (!element) return

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '200px', // Start loading before reaching the end
      threshold: 0,
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [handleObserver])

  if (products.length === 0 && !isFetchingNextPage) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-300">
        <Package size={32} className="mb-2" />
        <p className="font-medium">No products found</p>
      </div>
    )
  }

  const LoadingIndicator = () => (
    <div ref={loadMoreRef} className="col-span-full flex items-center justify-center py-8">
      {isFetchingNextPage ? (
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      ) : hasNextPage ? (
        <div className="h-8" /> // Invisible trigger for intersection observer
      ) : null}
    </div>
  )

  if (viewMode === 'list') {
    return (
      <div ref={containerRef} className="space-y-2 md:space-y-3">
        {products.map((product, index) => (
          <ProductRowCard
            key={product.id}
            product={product}
            onAddToCart={onAddToCart}
            onClick={onProductClick}
            isFocused={index === focusedIndex}
            cartQuantity={cartQuantities.get(product.id)}
          />
        ))}
        <LoadingIndicator />
      </div>
    )
  }

  return (
    <div ref={containerRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
      {products.map((product, index) => (
        <ProductCard
          key={product.id}
          product={product}
          onAddToCart={onAddToCart}
          onClick={onProductClick}
          isFocused={index === focusedIndex}
          cartQuantity={cartQuantities.get(product.id)}
        />
      ))}
      <LoadingIndicator />
    </div>
  )
}
